import React from 'react';

interface XReplAIBrandingProps {
  variant?: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showGlow?: boolean;
}

export function XReplAIBranding({ 
  variant = 'dark', 
  size = 'lg',
  showGlow = true 
}: XReplAIBrandingProps) {
  
  const sizeMap = {
    sm: { fontSize: '2rem', height: '80px' },
    md: { fontSize: '3rem', height: '120px' },
    lg: { fontSize: '4rem', height: '160px' },
    xl: { fontSize: '6rem', height: '240px' }
  };

  const dimensions = sizeMap[size];
  
  const colors = variant === 'dark' 
    ? {
        base: '#E0E7FF', // light indigo for base text
        emphasis: '#60A5FA', // bright blue for x and AI
        glow: '#60A5FA',
        background: 'linear-gradient(135deg, #1E3A8A 0%, #1E293B 100%)'
      }
    : {
        base: '#1E3A8A', // deep indigo for base text
        emphasis: '#3B82F6', // blue for x and AI
        glow: '#60A5FA',
        background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)'
      };

  return (
    <div 
      className="relative flex items-center justify-center overflow-hidden rounded-2xl"
      style={{ 
        background: colors.background,
        minHeight: dimensions.height,
        padding: '2rem'
      }}
    >
      {/* Animated background gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-20 blur-3xl"
          style={{ 
            background: `radial-gradient(circle, ${colors.emphasis} 0%, transparent 70%)`,
            animation: 'float 6s ease-in-out infinite'
          }}
        />
        <div 
          className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-20 blur-3xl"
          style={{ 
            background: `radial-gradient(circle, ${colors.emphasis} 0%, transparent 70%)`,
            animation: 'float 8s ease-in-out infinite reverse'
          }}
        />
      </div>

      {/* Main text */}
      <div className="relative z-10 flex items-center gap-0" style={{ fontSize: dimensions.fontSize }}>
        {/* 'x' - emphasized */}
        <span 
          className="font-bold tracking-tight transition-all duration-300 hover:scale-110"
          style={{ 
            color: colors.emphasis,
            fontWeight: 800,
            textShadow: showGlow ? `0 0 30px ${colors.glow}40, 0 0 60px ${colors.glow}20` : 'none',
            filter: showGlow ? 'brightness(1.2)' : 'none'
          }}
        >
          x
        </span>

        {/* 'Repl' - base style */}
        <span 
          className="font-semibold tracking-tight"
          style={{ 
            color: colors.base,
            fontWeight: 600
          }}
        >
          Repl
        </span>

        {/* 'AI' - emphasized */}
        <span 
          className="font-bold tracking-tight transition-all duration-300 hover:scale-110"
          style={{ 
            color: colors.emphasis,
            fontWeight: 800,
            textShadow: showGlow ? `0 0 30px ${colors.glow}40, 0 0 60px ${colors.glow}20` : 'none',
            filter: showGlow ? 'brightness(1.2)' : 'none'
          }}
        >
          AI
        </span>
      </div>

      {/* Subtle sparkles */}
      {showGlow && (
        <>
          <div 
            className="absolute top-1/4 left-1/4 w-2 h-2 rounded-full opacity-60"
            style={{ 
              background: colors.emphasis,
              animation: 'twinkle 3s ease-in-out infinite',
              boxShadow: `0 0 10px ${colors.emphasis}`
            }}
          />
          <div 
            className="absolute bottom-1/3 right-1/3 w-1 h-1 rounded-full opacity-60"
            style={{ 
              background: colors.emphasis,
              animation: 'twinkle 4s ease-in-out infinite 1s',
              boxShadow: `0 0 8px ${colors.emphasis}`
            }}
          />
          <div 
            className="absolute top-1/2 right-1/4 w-1.5 h-1.5 rounded-full opacity-60"
            style={{ 
              background: colors.emphasis,
              animation: 'twinkle 5s ease-in-out infinite 2s',
              boxShadow: `0 0 10px ${colors.emphasis}`
            }}
          />
        </>
      )}

      {/* Inline animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-20px, -20px) scale(1.1); }
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
      `}</style>
    </div>
  );
}

// Export with preset variants
export function XReplAIBrandingHero() {
  return <XReplAIBranding variant="dark" size="xl" showGlow={true} />;
}

export function XReplAIBrandingCard() {
  return <XReplAIBranding variant="light" size="lg" showGlow={true} />;
}

export function XReplAIBrandingCompact() {
  return <XReplAIBranding variant="dark" size="md" showGlow={false} />;
}
